import type { Components, Theme, CSSObject } from "@mui/material/styles";
import type { SemanticPalette } from "../../tokens/colorSemantic";
import { RadiusToken } from "../../tokens/radii";

type Rmh = SemanticPalette;

// Outer ring (and inner dot when checked) are circles inside a single SVG.
// `vectorEffect="non-scaling-stroke"` keeps the 1px outline visually crisp
// regardless of which size variant scales the SVG.
const UncheckedIcon = (
  <svg className="RmhRadio-svg" viewBox="0 0 20 20" aria-hidden="true">
    <circle
      className="RmhRadio-ring"
      cx="10"
      cy="10"
      r="9.5"
      strokeWidth="1"
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

const CheckedIcon = (
  <svg className="RmhRadio-svg" viewBox="0 0 20 20" aria-hidden="true">
    <circle
      className="RmhRadio-ring"
      cx="10"
      cy="10"
      r="9.5"
      strokeWidth="1"
      vectorEffect="non-scaling-stroke"
    />
    <circle className="RmhRadio-dot" cx="10" cy="10" r="6" />
  </svg>
);

type StateColors = {
  ring: string;
  dot?: string;
  focusShadow?: string;
};

type RadioColorSpec = {
  unchecked: {
    enabled: StateColors;
    hovered: StateColors;
    focused: StateColors;
    pressed: StateColors;
    disabled: StateColors;
  };
  checked: {
    enabled: StateColors;
    hovered: StateColors;
    focused: StateColors;
    pressed: StateColors;
    disabled: StateColors;
  };
};

// Each state writes two CSS custom properties on the root; the SVG circles
// read them via `var(--rmh-radio-…)`, which cascades cleanly without having
// to re-target the SVG elements per state.
const stateCss = (s: StateColors): CSSObject => {
  const css: CSSObject = { "--rmh-radio-ring": s.ring };
  if (s.dot) css["--rmh-radio-dot"] = s.dot;
  if (s.focusShadow) css.boxShadow = s.focusShadow;
  return css;
};

const UNCHECKED_NOT = ":not(.Mui-checked)";

const colorStyle =
  (build: (rmh: Rmh) => RadioColorSpec) =>
  ({ theme }: { theme: Theme }): CSSObject => {
    const spec = build(theme.vars!.palette.rmh);
    return {
      [`&${UNCHECKED_NOT}`]: stateCss(spec.unchecked.enabled),
      [`&:hover:not(.Mui-disabled)${UNCHECKED_NOT}`]: stateCss(
        spec.unchecked.hovered
      ),
      [`&.Mui-focusVisible${UNCHECKED_NOT}`]: stateCss(spec.unchecked.focused),
      [`&:active:not(.Mui-disabled)${UNCHECKED_NOT}`]: stateCss(
        spec.unchecked.pressed
      ),
      [`&.Mui-disabled${UNCHECKED_NOT}`]: stateCss(spec.unchecked.disabled),

      "&.Mui-checked": stateCss(spec.checked.enabled),
      "&.Mui-checked:hover:not(.Mui-disabled)": stateCss(spec.checked.hovered),
      "&.Mui-checked.Mui-focusVisible": stateCss(spec.checked.focused),
      "&.Mui-checked:active:not(.Mui-disabled)": stateCss(spec.checked.pressed),
      "&.Mui-checked.Mui-disabled": stateCss(spec.checked.disabled),
    };
  };

const primarySpec = (rmh: Rmh): RadioColorSpec => ({
  unchecked: {
    enabled: { ring: rmh.border["4-faint"].rest },
    hovered: { ring: rmh.border["4-faint"].hover },
    focused: {
      ring: rmh.border["1-surface"].hover,
      focusShadow: rmh.focus.primary,
    },
    pressed: { ring: rmh.border["1-surface"].active },
    disabled: { ring: rmh.stateBorder.disabled },
  },
  checked: {
    enabled: {
      ring: rmh.border["5-brand"].active, // blue/700
      dot: rmh.border["5-brand"].hover, // blue/600 — per the SVG: darker ring, lighter dot
    },
    hovered: {
      ring: rmh.border["5-brand"].active,
      dot: rmh.border["5-brand"].hover,
    },
    focused: {
      ring: rmh.border["1-surface"].hover,
      dot: rmh.border["5-brand"].hover,
      focusShadow: rmh.focus.primary,
    },
    pressed: {
      ring: rmh.border["1-surface"].active,
      dot: rmh.border["5-brand"].hover,
    },
    disabled: { ring: rmh.stateBorder.disabled, dot: rmh.stateBorder.disabled },
  },
});

const errorSpec = (rmh: Rmh): RadioColorSpec => ({
  unchecked: {
    enabled: { ring: rmh.border["8-error"].rest },
    hovered: { ring: rmh.border["8-error"].hover },
    focused: {
      ring: rmh.border["8-error"].hover,
      focusShadow: rmh.focus.error,
    },
    pressed: { ring: rmh.border["8-error"].active },
    disabled: { ring: rmh.stateBorder.disabled },
  },
  checked: {
    enabled: {
      ring: rmh.border["8-error"].hover,
      dot: rmh.border["8-error"].hover,
    },
    hovered: {
      ring: rmh.border["8-error"].hover,
      dot: rmh.border["8-error"].hover,
    },
    focused: {
      ring: rmh.border["8-error"].hover,
      dot: rmh.border["8-error"].hover,
      focusShadow: rmh.focus.error,
    },
    pressed: {
      ring: rmh.border["8-error"].active,
      dot: rmh.border["8-error"].hover,
    },
    disabled: { ring: rmh.stateBorder.disabled, dot: rmh.stateBorder.disabled },
  },
});

export const MuiRadio: NonNullable<Components<Theme>["MuiRadio"]> = {
  defaultProps: {
    disableRipple: true,
    color: "primary",
    size: "medium",
    icon: UncheckedIcon,
    checkedIcon: CheckedIcon,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      padding: 0,
      // Round the root so the focus boxShadow projects as a circle.
      borderRadius: RadiusToken.MAX,
      transition: theme.transitions.create(["box-shadow"], {
        duration: theme.transitions.duration.shortest,
      }),
      "& .RmhRadio-svg": {
        width: "100%",
        height: "100%",
        display: "block",
      },
      "& .RmhRadio-ring": {
        fill: theme.vars!.palette.rmh.paper["1-surface"].rest,
        stroke: "var(--rmh-radio-ring)",
        transition: theme.transitions.create("stroke", {
          duration: theme.transitions.duration.shortest,
        }),
      },
      "& .RmhRadio-dot": {
        fill: "var(--rmh-radio-dot)",
        transition: theme.transitions.create("fill", {
          duration: theme.transitions.duration.shortest,
        }),
      },
    }),
  },
  variants: [
    { props: { size: "small" }, style: { width: "1rem", height: "1rem" } },
    {
      props: { size: "medium" },
      style: { width: "1.25rem", height: "1.25rem" },
    },
    {
      props: { size: "large" },
      style: { width: "1.75rem", height: "1.75rem" },
    },
    { props: { color: "primary" }, style: colorStyle(primarySpec) },
    { props: { color: "error" }, style: colorStyle(errorSpec) },
  ],
};
